import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Church, State } from "baptize-america-client";
import Map, { MapRef, NavigationControl } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowCircleRight, GpsFix } from "@phosphor-icons/react";

import { useUserData } from "../components/DataProvider";
import { useChurches } from "../contexts/ChurchesProvider";
import { STATE_CENTERS, US_CENTER } from "../components/MapConstants";
import { UserLocation } from "../hooks/useUserLocation";
import { formatAddress, getEnvVar, isAdmin } from "../utils/helper";

const token = window.location.hostname.endsWith("baptize.org")
  ? getEnvVar("REACT_APP_MAPBOX_TOKEN_PROD")
  : getEnvVar("REACT_APP_MAPBOX_TOKEN_DEV");

if (!token) {
  console.error(
    "Mapbox access token is missing. Check your environment variables.",
  );
} else {
  mapboxgl.accessToken = token;
}

interface Coordinate {
  lng: number;
  lat: number;
  label?: string; // church name
  address: string;
  churchId: string;
}
interface MapViewProps {
  churches: Church[];
  userLocation: UserLocation;
  selectedState: State | "ALL";
}

const geocodeCache: Record<string, Coordinate> = {};

const Mapbox: React.FC<MapViewProps> = ({
  churches,
  userLocation,
  selectedState,
}) => {
  const navigate = useNavigate();
  const mapRef = useRef<MapRef>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { locations, setLocations } = useChurches();
  const [selectedLocation, setSelectedLocation] = useState<Coordinate | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { attributes } = useUserData();
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  const flyToUserLocation = useCallback(() => {
    if (
      (userLocation.permission === "granted" ||
        userLocation.permission === "prompt") &&
      userLocation.latitude != null &&
      userLocation.longitude != null &&
      mapRef.current
    ) {
      mapRef.current.getMap().flyTo({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: 5.5,
        essential: true,
      });
    } else {
      if (selectedState !== "ALL" && mapRef.current) {
        mapRef.current.getMap().flyTo({
          center: [
            STATE_CENTERS[selectedState].LONGITUDE,
            STATE_CENTERS[selectedState].LATITUDE,
          ],
          zoom: 5.5,
          essential: true,
        });
        return;
      }
      mapRef.current?.getMap().flyTo({
        center: [US_CENTER.LONGITUDE, US_CENTER.LATITUDE],
        zoom: 2.5,
        essential: true,
      });
    }
  }, [userLocation, selectedState]);

  useEffect(() => {
    if (locations.length > 0) {
      setLoading(false);
      return;
    }
    // Geocode addresses using Mapbox Geocoding API
    const geocodeAddresses = async () => {
      setLoading(true);

      if (!churches || churches.length === 0) {
        setLoading(false);
        return;
      }
      const fetchCoordinates = churches.map(async (church) => {
        const address = church.address
          ? formatAddress(church.address, true)
          : "";
        if (!address) return null;

        const churchId = church.churchId ?? "";
        if (
          geocodeCache[churchId] &&
          geocodeCache[churchId].address === address
        ) {
          return geocodeCache[churchId]; // cached result
        }

        try {
          const res = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxgl.accessToken}`,
          );
          const data = await res.json();

          if (data.features?.[0]) {
            const [lng, lat] = data.features[0].center;
            const coord: Coordinate = {
              lng,
              lat,
              label: church.name?.trim() ?? "",
              address,
              churchId,
            };
            geocodeCache[churchId] = coord;
            return coord;
          }
        } catch (err) {
          console.error("Geocoding failed:", address, err);
        }
        return null; // fallback for failed or invalid results
      });
      const results = await Promise.all(fetchCoordinates);
      const coords = results.filter((coord): coord is Coordinate => !!coord); // remove nulls
      setLocations(coords);
      setLoading(false);
    };
    geocodeAddresses();
  }, [churches, locations.length, setLocations]);

  // Add Church Location markers when map + data is ready
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || locations.length === 0) {
      return;
    }
    const map = mapRef.current.getMap();
    // Clean up old markers before adding new markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    locations.forEach((loc) => {
      const marker = new mapboxgl.Marker({ color: "#00169E" })
        .setLngLat([loc.lng, loc.lat])
        .addTo(map);
      marker.getElement().style.cursor = "pointer";
      marker.getElement().addEventListener("click", () => {
        setSelectedLocation(loc);
      });
      marker
        .getElement()
        .setAttribute("aria-label", `Location for ${loc.label ?? "Church"}`);

      markersRef.current.push(marker);
    });
  }, [locations, mapLoaded]);

  // Cleanup markers when the component unmounts
  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
    };
  }, []);

  useEffect(() => {
    flyToUserLocation();
  }, [selectedState, flyToUserLocation]);

  // Do not render if token is missing
  if (!mapboxgl.accessToken) {
    return isAdmin(attributes) ? (
      <Box
        sx={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #ccc",
          borderRadius: 2,
        }}
      >
        <Typography color="text.secondary">
          Map is unavailable — missing Mapbox token.
        </Typography>
      </Box>
    ) : null;
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", height: 350, width: "100%" }}>
      <Map
        key={churches.map((c) => c.churchId).join(",")} // re-mounts Map on data change
        ref={mapRef}
        mapboxAccessToken={mapboxgl.accessToken!}
        initialViewState={{
          longitude: US_CENTER.LONGITUDE,
          latitude: US_CENTER.LATITUDE,
          zoom: 2.5,
        }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        style={{ width: "100%", height: "100%", borderRadius: 8 }}
        onClick={(e) => {
          const clickedEl = e.originalEvent.target as HTMLElement;
          const isInsideCard = cardRef.current?.contains(clickedEl);
          const isMarker = clickedEl.closest(".mapboxgl-marker");

          if (!isInsideCard && !isMarker) {
            setSelectedLocation(null);
          }
        }}
        onLoad={() => setMapLoaded(true)}
        aria-label="Interactive map showing church locations"
      >
        <Tooltip
          title={
            userLocation.permission === "denied"
              ? "Location permission denied"
              : userLocation.latitude == null
                ? "Location not available"
                : "Center map on your location"
          }
          placement="top-end" // 👈 Tooltip shows above the button
          arrow // 👈 Adds a small arrow pointing down
        >
          <IconButton
            onClick={flyToUserLocation}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 1000,
              bgcolor: "white",
              boxShadow: 2,
              "&:hover": {
                bgcolor: "#f0f0f0",
              },
            }}
            aria-label="Center map on your location"
          >
            <GpsFix size={20} />
          </IconButton>
        </Tooltip>

        <NavigationControl position="top-left" />
        {selectedLocation && (
          <Box
            ref={cardRef}
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 3,
              p: 2,
              zIndex: 1000,
              cursor: "pointer",
              width: "90%",
              maxWidth: 300,
            }}
            aria-label={`Church details for ${selectedLocation.label}`}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.25,
              }}
            >
              {selectedLocation.label && (
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                  }}
                >
                  {selectedLocation.label}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {selectedLocation.address}
                </Typography>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/churches/${selectedLocation.churchId}`);
                  }}
                  sx={{
                    color: "#00169E",
                  }}
                  aria-label={`View details for ${selectedLocation.label}`}
                >
                  <ArrowCircleRight size={24} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </Map>
    </Box>
  );
};

export default Mapbox;
